import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Table, Button, message, Card, Typography, Empty, Space, Select, Col, Input, Form } from 'antd';
import { ReloadOutlined, ExportOutlined } from '@ant-design/icons';
import { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import FilterPanel from '../../components/common/FilterPanel';
import { storeItemInventoryService } from '../../services/storeItemInventoryService';
import { itemService } from '../../services/itemService';
import { warehouseService } from '../../services/warehouseService';
import dayjs from 'dayjs';
import '../../styles/storeItemInventory.css';
import {
  StoreItemInventory,
  StoreItemInventoryFilters,
  StoreItemInventoryPaginationParams,
  StoreItemInventorySortField,
  SortOrder,
  Item,
  Store,
  DEFAULT_QUANTITY_CONFIG
} from '../../types/store-item-inventory';

const { Title } = Typography;
const { Option } = Select;

const StoreItemInventoryList: React.FC = () => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  // State management
  const [loading, setLoading] = useState<boolean>(false);
  const [storeOptionsLoading, setStoreOptionsLoading] = useState<boolean>(false);
  const [itemOptionsLoading, setItemOptionsLoading] = useState<boolean>(false);
  const [inventories, setInventories] = useState<StoreItemInventory[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [current, setCurrent] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(20);
  const [filters, setFilters] = useState<StoreItemInventoryFilters>({});
  const [sortField, setSortField] = useState<StoreItemInventorySortField>();
  const [sortOrder, setSortOrder] = useState<'ascend' | 'descend'>('descend');
  
  // Options for dropdowns
  const [storeOptions, setStoreOptions] = useState<Store[]>([]);
  const [itemOptions, setItemOptions] = useState<Item[]>([]);

  // Fetch inventories data
  const fetchInventories = async () => {
    setLoading(true);
    try {
      const response = await storeItemInventoryService.getStoreItemInventories(
        filters,
        {
          currentPage: current,
          pageSize,
          // 移除排序参数，因为现在在前端排序
        }
      );

      if (response.success && response.data) {
        setInventories(response.data.tableData);
        setTotal(response.data.paging.totalCount);
      } else {
        console.error(t('storeItemInventory.list.messages.dataFormatError'), response);
        setInventories([]);
        setTotal(0);
        if (response.errorMessage) {
          message.error(response.errorMessage);
        }
      }
    } catch (error) {
      message.error(t('storeItemInventory.list.messages.fetchFailed'));
      console.error(t('storeItemInventory.list.messages.fetchFailed'), error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch store options
  const fetchStoreOptions = async (searchValue: string = '') => {
    setStoreOptionsLoading(true);
    try {
      const response = await warehouseService.listWarehouse({searchStr:searchValue});
      if (response.success && response.data) {
        setStoreOptions(response.data);
      }
    } catch (error) {
      console.error(t('storeItemInventory.list.messages.fetchStoreOptionsFailed'), error);
    } finally {
      setStoreOptionsLoading(false);
    }
  };

  // Fetch item options
  const fetchItemOptions = async (searchValue: string = '') => {
    setItemOptionsLoading(true);
    try {
      let req = {
        searchStr: searchValue,
        pageSize: 100,
        currentPage: 1
      };
      const response = await itemService.listItems(req);
      if (response.success && response.data) {
        setItemOptions(response.data);
      }
    } catch (error) {
      console.error(t('storeItemInventory.list.messages.fetchItemOptionsFailed'), error);
    } finally {
      setItemOptionsLoading(false);
    }
  };

  const handleTableChange = (
    pagination: any,
    _filters: any,
    sorter: any
  ) => {
    setCurrent(pagination.current);
    setPageSize(pagination.pageSize);

    // 更新排序状态，但不触发后端请求
    if (sorter.field && sorter.order) {
      setSortField(sorter.field);
      setSortOrder(sorter.order);
    }
  };

  // 使用 useMemo 实现前端排序
  const sortedInventories = useMemo(() => {
    if (!sortField || !sortOrder) {
      return inventories;
    }

    return [...inventories].sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];

      // 处理日期和数字类型的排序
      if (sortField === 'gmtCreate' || sortField === 'gmtModified') {
        aValue = new Date(aValue).getTime();
        bValue = new Date(bValue).getTime();
      }

      if (sortOrder === 'ascend') {
        return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
      } else {
        return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
      }
    });
  }, [inventories, sortField, sortOrder]);

  const handleSearch = (values: any) => {
    setFilters({ ...filters, ...values });
    setCurrent(1); // 重置到第一页
  };

  const handleReset = () => {
    form.resetFields();
    setFilters({});
    setCurrent(1);
  };


  // Format datetime display
  const formatDateTime = (timestamp: number): string => {
    return dayjs(timestamp).format('YYYY-MM-DD HH:mm:ss');
  };

  // Format quantity display
  const formatQuantity = (quantity: number): string => {
    if (DEFAULT_QUANTITY_CONFIG.thousandSeparator) {
      return quantity.toLocaleString('zh-CN', {
        minimumFractionDigits: DEFAULT_QUANTITY_CONFIG.decimalPlaces,
        maximumFractionDigits: DEFAULT_QUANTITY_CONFIG.decimalPlaces
      });
    }
    return quantity.toFixed(DEFAULT_QUANTITY_CONFIG.decimalPlaces);
  };

  // 移除 useEffect 中的排序依赖
  useEffect(() => {
    fetchInventories();
  }, [current, pageSize, filters]); // 移除了 sortField, sortOrder 依赖

  useEffect(() => {
    fetchStoreOptions();
    fetchItemOptions();
  }, []);

  const columns: ColumnsType<StoreItemInventory> = [
    {
      title: t('storeItemInventory.list.columns.storeCode'),
      dataIndex: 'storeCode',
      key: 'storeCode',
      // sorter: true,
      // sortOrder: sortField === 'storeCode' ? sortOrder : null,
      width: 120,
    },
    {
      title: t('storeItemInventory.list.columns.itemCode'),
      dataIndex: 'itemCode',
      key: 'itemCode',
      // sorter: true,
      // sortOrder: sortField === 'itemCode' ? sortOrder : null,
      width: 120,
    },
    {
      title: t('storeItemInventory.list.columns.itemName'),
      dataIndex: 'itemName',
      key: 'itemName',
      // sorter: true,
      // sortOrder: sortField === 'itemCode' ? sortOrder : null,
      width: 150,
    },
    {
      title: t('storeItemInventory.list.columns.ownerCode'),
      dataIndex: 'ownerCode',
      key: 'ownerCode',
      // sorter: true,
      // sortOrder: sortField === 'ownerCode' ? sortOrder : null,
      width: 120,
    },
    {
      title: t('storeItemInventory.list.columns.quantity'),
      dataIndex: 'quantity',
      key: 'quantity',
      // sorter: true,
      // sortOrder: sortField === 'quantity' ? sortOrder : null,
      width: 120,
      render: (quantity: number) => formatQuantity(quantity),
    },
    {
      title: t('storeItemInventory.list.columns.createTime'),
      dataIndex: 'gmtCreate',
      key: 'gmtCreate',
      // sorter: true,
      // sortOrder: sortField === 'gmtCreate' ? sortOrder : null,
      width: 180,
      render: (timestamp: number) => formatDateTime(timestamp),
    },
    {
      title: t('storeItemInventory.list.columns.modifyTime'),
      dataIndex: 'gmtModified',
      key: 'gmtModified',
      // sorter: true,
      // sortOrder: sortField === 'gmtModified' ? sortOrder : null,
      width: 180,
      render: (timestamp: number) => formatDateTime(timestamp),
    },
  ];

  return (
    <div>
      <FilterPanel
        title={t("menu.storeItemInventory")}
        form={form}
        onFilter={handleSearch}
        onReset={handleReset}
        className="store-item-inventory-filter-panel"
      >
        <Col span={6}>
          <Form.Item label={t('storeItemInventory.list.filter.store')} name="storeCode">
            <Select
              placeholder={t('storeItemInventory.list.filter.storePlaceholder')}
              allowClear
              showSearch
              loading={storeOptionsLoading}
              onSearch={fetchStoreOptions}
              filterOption={false}
              notFoundContent={storeOptionsLoading ? t('storeItemInventory.list.filter.loading') : t('storeItemInventory.list.filter.noData')}
            >
              {storeOptions.map((store) => (
                <Option key={store.code} value={store.code}>
                  {store.code} - {store.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item label={t('storeItemInventory.list.filter.item')} name="itemCode">
            <Select
              placeholder={t('storeItemInventory.list.filter.itemPlaceholder')}
              allowClear
              showSearch
              loading={itemOptionsLoading}
              onSearch={fetchItemOptions}
              filterOption={false}
              notFoundContent={itemOptionsLoading ? t('storeItemInventory.list.filter.loading') : t('storeItemInventory.list.filter.noData')}
            >
              {itemOptions.map((item) => (
                <Option key={item.code} value={item.code}>
                  {item.code} - {item.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
      </FilterPanel>
      <Card>      

        <div>
          {sortedInventories.length === 0 && !loading ? (
            <Empty
              description={t('storeItemInventory.list.empty.description')}
              style={{ margin: '40px 0' }}
            />
          ) : (
            <Table
              columns={columns}
              dataSource={sortedInventories}
              rowKey="id"
              loading={loading}
              pagination={{
                current,
                pageSize,
                total,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) =>
                  t('storeItemInventory.list.pagination.displayRange', { start: range[0], end: range[1], total }),
                pageSizeOptions: ['20', '50', '100'],
              }}
              onChange={handleTableChange}
              scroll={{ x: 1200, y: 500 }}
            />
          )}
        </div>
      </Card>
    </div>
  );
};

export default StoreItemInventoryList;